import { ComponentProps, FC, memo, useCallback, useRef, useState } from "react";

import { Stack } from "@mui/material";
import { getTicketRates, TicketingFormCategory } from "@simplyk/common";
import { FormProvider, useForm, UseFormReturn, useWatch } from "react-hook-form";

import { CapacityTotalsDialog } from "./CapacityTotalsDialog/CapacityTotalsDialog";
import { CapacityShortfall, getCapacityShortfall } from "./CapacityTotalsDialog/getCapacityShortfall";
import { Rates } from "./Rates/Rates";

import { isMultidateTicketing } from "@/common/helpers/ticketing";
import { toFiniteNumber } from "@/common/helpers/toFiniteNumber";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { mapRateOutputsToRateData } from "@/common/helpers/form-edition";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { RateByOccurrenceOutput, RateOutput } from "@/common/types/trpc";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { useAnimatePreviewOnRateEvents } from "@/features/LiveFormEditor/AnimatePreview/useAnimatePreviewOnRateEvents";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useLatestRef } from "@/features/LiveFormEditor/common/useLatestRef";
import { getPlaceholderRates } from "@/features/LiveFormEditor/common/prefillDataBuilders/prefillRates";
import { useUpdateTicketingDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";
import { mapDataToEditRates } from "@/features/LiveFormEditor/Steps/helpers/mapper";
import { useUploadRemainingRatePhotos } from "@/features/LiveFormEditor/Steps/helpers/useUploadRemainingRatePhotos";
import { buildPreviewShopVariantsAndChildren } from "@/features/LiveFormEditor/Steps/StepTickets/helpers/expandVariantsForPreview";
import { useBuildDefaultRate } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useBuildDefaultRate";
import { useSaveRateOnCollapse } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useSaveRateOnCollapse";
import {
  RateImageEditorsProvider,
  useRateImageEditorsRegistry,
} from "@/features/LiveFormEditor/Steps/StepTickets/Rates/RateImageEditors/RateImageEditorsContext";
import { RatesDigestProvider } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/RatesDigest/RatesDigestContext";
import { getStepTranslations } from "@/features/LiveFormEditor/Steps/StepTickets/stepTickets.helper";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";
import { Dot } from "@/icons/outlined";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { isMin, isRequired } from "@/common/helpers/validators";
import {
  isSweepstakesBaseEntryRateRequired,
  SWEEPSTAKES_FREE_ENTRY_URL,
} from "@/features/LiveFormEditor/common/sweepstakes";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

const buildRateSignature = (rate: unknown): string =>
  JSON.stringify(rate, (_key: string, value: unknown) => {
    if (typeof value !== "string" || value.length <= 200 || !value.startsWith("data:")) {
      return value;
    }
    const { length } = value;
    const quarter = value.slice(length >> 2, (length >> 2) + 24);
    const half = value.slice(length >> 1, (length >> 1) + 24);
    return `data:${length}:${quarter}:${half}:${value.slice(-24)}`;
  });

interface RatesSectionProps {
  form: UseFormReturn<StepTicketsFormData>;
  onRateCollapsed: (rateId: string) => void;
  imageEditorsRegistry: ComponentProps<typeof RateImageEditorsProvider>["value"];
}

const RatesSection = memo(({ form, onRateCollapsed, imageEditorsRegistry }: RatesSectionProps) => (
  <FormProvider {...form}>
    <RateImageEditorsProvider value={imageEditorsRegistry}>
      <RatesDigestProvider>
        <Rates form={form} onRateCollapsed={onRateCollapsed} />
      </RatesDigestProvider>
    </RateImageEditorsProvider>
  </FormProvider>
));
RatesSection.displayName = "RatesSection";

export const TicketingLiveEditorStepTickets: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketing } = useLiveFormEditorContext<"ticketing">();
  const { logUserInputEvent } = useLiveFormAmplitudeEvents();
  const { logAmplitudeEvent } = useAmplitude();
  const formCategory = apiTicketing.formCategory;
  const isSweepstakes = isSweepstakesCategory(formCategory);
  const { organization, organizationCurrency } = useCurrentUserContext();
  const requiresBaseEntryRate = isSweepstakesBaseEntryRateRequired(organization?.country, organization?.type);
  const buildDefaultRate = useBuildDefaultRate();

  const ticketRates = getTicketRates(apiTicketing.rates);

  const defaultValues: StepTicketsFormData = {
    seats: apiTicketing.seats?.toString() || "",
    rates: (ticketRates.length
      ? mapRateOutputsToRateData(ticketRates, apiTicketing.locale)
      : [buildDefaultRate({ ratesLength: 0 })]
    ).map((rate) =>
      formCategory === TicketingFormCategory.Lottery
        ? { ...rate, attendeesByTicket: rate.attendeesByTicket ?? 1 }
        : rate
    ),
    ratesToDelete: [],
    imageIsUploading: false,
    sweepstakesBaseEntryRate: apiTicketing.sweepstakesBaseEntryRate ?? undefined,
  };

  const form = useForm<StepTicketsFormData>({ defaultValues });
  const { errors } = form.formState;
  const isUploading = useWatch({ control: form.control, name: "imageIsUploading" });

  const {
    registryValue: rateImageEditorsRegistry,
    finalizePendingImageEditors,
    finalizeImageEditor,
  } = useRateImageEditorsRegistry();

  const previewRatesCacheRef = useRef<Map<string, { signature: string; rate: RateOutput }>>(new Map());

  const updateRatesAndSeats = trpc.updateTicketingRatesAndSeats.useMutation();
  const utils = trpc.useUtils();
  const uploadRemainingRatePhotos = useUploadRemainingRatePhotos();

  const { saveRate, waitForPendingRateSave } = useSaveRateOnCollapse({
    form,
    apiTicketing,
    finalizeImageEditor,
  });

  const saveRateRef = useLatestRef(saveRate);
  const onRateCollapsed = useCallback((rateId: string) => void saveRateRef.current(rateId), [saveRateRef]);

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepTicketsFormData>({
    type: "ticketing",
    form,
    saveData: async (formData) => {
      await waitForPendingRateSave();

      const membershipIdsByRate = formData.rates?.map((rate) => rate.memberOnlyMembershipRateIds) ?? [];

      await finalizePendingImageEditors();
      await uploadRemainingRatePhotos({
        rates: form.getValues("rates"),
        setPhotoUrl: (rateIndex, photoIndex, photoUrl) =>
          form.setValue(`rates.${rateIndex}.photos.${photoIndex}.photoUrl` as const, photoUrl, { shouldDirty: true }),
      });

      const updatedFormData = form.getValues();

      const ratesWithMembershipIds = updatedFormData.rates?.map((rate, index) => ({
        ...rate,
        memberOnlyMembershipRateIds: membershipIdsByRate[index] ?? rate.memberOnlyMembershipRateIds,
      }));

      const saved = await updateRatesAndSeats.mutateAsync({
        ticketingId: apiTicketing.id,
        seats: updatedFormData.seats ? parseInt(updatedFormData.seats, 10) : null,
        rates: ratesWithMembershipIds ? mapDataToEditRates(ratesWithMembershipIds, apiTicketing) : undefined,
        ratesToDelete: updatedFormData.ratesToDelete,
        ...(isSweepstakes && {
          sweepstakesBaseEntryRate: requiresBaseEntryRate ? (updatedFormData.sweepstakesBaseEntryRate ?? null) : null,
        }),
      });

      // The questions step reads its own cached query, and a rates save can change what it must show:
      // deleting a type archives the questions only asked for it, and adding one hands it the questions
      // asked for every type. Without this it keeps showing questions the server has already archived.
      await utils.getTicketingQuestionsStepData.invalidate({ ticketingId: apiTicketing.id });

      return saved;
    },
  });

  const [capacityShortfall, setCapacityShortfall] = useState<CapacityShortfall | null>(null);
  const [isCapacityDialogOpen, setIsCapacityDialogOpen] = useState(false);
  const isCapacityConfirmingRef = useRef(false);

  // Spec: intercept the Next click when the global capacity is lower than the sum
  // of tickets for sale across rates.
  const handleNext = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      const formData = form.getValues();

      const shortfall = getCapacityShortfall(formData.seats, formData.rates ?? [], new Date());
      if (shortfall) {
        setCapacityShortfall(shortfall);
        setIsCapacityDialogOpen(true);
        logAmplitudeEvent(AmplitudeEvents.TicketCapacityWarningShown, {
          form_type: formCategory,
          overall_seats: shortfall.overallSeats,
          rate_capacity: shortfall.sellableTotal,
        });
        return;
      }
    }
    await handleSaveAndNext();
  };

  const handleCapacityConfirm = async () => {
    if (isCapacityConfirmingRef.current) {
      return;
    }
    isCapacityConfirmingRef.current = true;
    logAmplitudeEvent(AmplitudeEvents.TicketCapacityWarningResolved, {
      action: "confirmed_sold_out",
      form_type: formCategory,
    });
    setIsCapacityDialogOpen(false);
    try {
      await handleSaveAndNext();
    } finally {
      isCapacityConfirmingRef.current = false;
    }
  };

  const handleCapacityAdjust = () => {
    logAmplitudeEvent(AmplitudeEvents.TicketCapacityWarningResolved, {
      action: "adjusted_numbers",
      form_type: formCategory,
    });
    setIsCapacityDialogOpen(false);
  };

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.ticketing.seats = formData.seats ? parseInt(formData.seats, 10) : null;

      if (!formData.rates) {
        liveDataToMutate.ticketing.rates = getPlaceholderRates({ formCategory, t, locale: apiTicketing.locale });
      }

      const rates = formData.rates && mapDataToEditRates(formData.rates, apiTicketing);
      const ratesWithVariants =
        rates &&
        rates.map((rate, index) => {
          const source = formData.rates?.[index];
          if (!source) {
            return rate;
          }
          const { shopVariants, childRatesInfo } = buildPreviewShopVariantsAndChildren({
            rateId: rate.id,
            shopVariants: source.shopVariants,
            variantSeats: source.variantSeats,
            locale: apiTicketing.locale,
          });
          return { ...rate, shopVariants, childRatesInfo };
        });
      const previousRatesCache = previewRatesCacheRef.current;
      const nextRatesCache = new Map<string, { signature: string; rate: RateOutput }>();
      const stabilizedRates = ratesWithVariants?.map((rate) => {
        const signature = buildRateSignature(rate);
        const cached = previousRatesCache.get(rate.id);
        const stableRate = cached && cached.signature === signature ? cached.rate : (rate as RateOutput);
        nextRatesCache.set(rate.id, { signature, rate: stableRate });
        return stableRate;
      });
      previewRatesCacheRef.current = nextRatesCache;
      liveDataToMutate.ticketing.rates = stabilizedRates as RateOutput[];

      if (liveDataToMutate.selectedOccurrenceWithRates && stabilizedRates) {
        liveDataToMutate.selectedOccurrenceWithRates.rates = stabilizedRates.flatMap((r) => {
          const parentEntry: RateByOccurrenceOutput = {
            rateId: r.id,
            remainingTickets: (r.seats ?? 1) * (r.attendeesByTicket ?? 1),
            soldTicketsCount: 0,
          } as RateByOccurrenceOutput;
          const childRatesInfo =
            (r as { childRatesInfo?: Array<{ childRateId: string; seats?: number | null }> }).childRatesInfo ?? [];
          const childEntries: RateByOccurrenceOutput[] = childRatesInfo.map(
            (child) =>
              ({
                rateId: child.childRateId,
                remainingTickets: child.seats ?? 1,
                soldTicketsCount: 0,
              }) as RateByOccurrenceOutput
          );
          return [parentEntry, ...childEntries];
        });
      }
    },
  });

  const formContainerRef = useRef<HTMLDivElement>(null);
  useAnimatePreviewOnRateEvents(formContainerRef, form);

  const ticketingHasMultipleDates = isMultidateTicketing(formCategory, apiTicketing.occurrences?.length ?? 0);

  const stepTranslations = getStepTranslations(formCategory, t);

  const capacityKeys = {
    sweepstakes: {
      label: "sweepstakes.capacity.label",
      perDateLabel: "sweepstakes.capacity.label",
      tooltip: "sweepstakes.capacity.tooltip",
      title: "sweepstakes.capacity.title",
    },
    custom: {
      label: "ticketing.ticketingSeatsItem",
      perDateLabel: "ticketing.ticketingSeatsItemPerDate",
      tooltip: "ticketing.ticketingSeatsItemTooltip",
      title: "ticketing.globalEventCapacityItem",
    },
    lottery: {
      label: "ticketing.ticketingSeats",
      perDateLabel: "ticketing.ticketingSeatsPerDate",
      tooltip: "ticketing.ticketingSeatsTooltip",
      title: "ticketing.globalEventCapacity.lottery",
    },
    default: {
      label: "ticketing.ticketingSeats",
      perDateLabel: "ticketing.ticketingSeatsPerDate",
      tooltip: "ticketing.ticketingSeatsTooltip",
      title: "ticketing.globalEventCapacity",
    },
  } as const;
  const capacityCategory = isSweepstakes
    ? "sweepstakes"
    : formCategory === TicketingFormCategory.Custom
      ? "custom"
      : formCategory === TicketingFormCategory.Lottery
        ? "lottery"
        : "default";
  const capacityTranslations = capacityKeys[capacityCategory];
  const seatsLabelKey = capacityTranslations[ticketingHasMultipleDates ? "perDateLabel" : "label"];

  const hasSeats =
    formCategory === TicketingFormCategory.Event ||
    formCategory === TicketingFormCategory.Lottery ||
    isSweepstakes ||
    formCategory === TicketingFormCategory.Custom;

  const totalTicketsSold = ticketRates.reduce((total, rate) => total + (rate.alreadyBoughtTicketsCount || 0), 0);
  const isRegistration = Boolean(apiTicketing.donationForm?.campaignId);

  const seatsValue = useWatch({ control: form.control, name: "seats" });
  const globalCapacity = toFiniteNumber(seatsValue);
  const hasGlobalCapacity = globalCapacity !== null && globalCapacity >= 0;
  const showsCapacityRatio = hasGlobalCapacity && !ticketingHasMultipleDates;

  const soldKeys = {
    sweepstakes: { total: "sweepstakes.totalEntries", capacity: "sweepstakes.totalEntriesOfCapacity" },
    custom: { total: "ticketing.totalTicketsSoldItem", capacity: "ticketing.totalTicketsSoldOfCapacityItem" },
    registration: {
      total: "ticketing.totalTicketsSoldRegistration",
      capacity: "ticketing.totalTicketsSoldOfCapacityRegistration",
    },
    default: { total: "ticketing.totalTicketsSold", capacity: "ticketing.totalTicketsSoldOfCapacity" },
  } as const;
  const soldCategory = isSweepstakes
    ? "sweepstakes"
    : formCategory === TicketingFormCategory.Custom
      ? "custom"
      : isRegistration
        ? "registration"
        : "default";
  const totalSoldKey = soldKeys[soldCategory][showsCapacityRatio ? "capacity" : "total"];

  return (
    <FormStepContainer
      title={stepTranslations.title}
      description={stepTranslations.description}
      form={form}
      animatePreview={false}
      actionsProps={{
        onBack: handleSaveAndBack,
        onNext: handleNext,
        disableNext: form.formState.isSubmitting || isUploading,
        disableBack: form.formState.isSubmitting || isUploading,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={5} ref={formContainerRef}>
          {hasSeats && (
            <LiveEditorBlock>
              <Stack spacing={isSweepstakes ? 1.5 : 1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <DashboardTypography variant="subtitle1" vibe="text-neutral-intense">
                    {t("dashboard", capacityTranslations.title)}
                  </DashboardTypography>
                  <DashboardChip
                    shape="rounded"
                    vibe="neutral"
                    icon={<Dot />}
                    slotProps={{
                      icon: {
                        vibe: "text-positive-quiet",
                        sx: { width: 12, height: 12, "& > svg": { transform: "scale(2)" } },
                      },
                    }}
                    label={t("dashboard", totalSoldKey, {
                      count: totalTicketsSold,
                      capacity: showsCapacityRatio ? globalCapacity : undefined,
                    })}
                    data-test="ticketing-total-sold-chip"
                  />
                </Stack>
                <DashboardTextField
                  name="seats"
                  label={t("dashboard", seatsLabelKey)}
                  tooltipText={t("dashboard", capacityTranslations.tooltip)}
                  control={form.control}
                  errorMessage={errors["seats"]?.message}
                  type="number"
                  inputProps={{ min: "0", max: "100000", step: "1" }}
                  placeholder={t("dashboard", "ticketing.ticketingSeatsPlaceholder")}
                  rules={
                    isSweepstakes
                      ? [
                          isRequired(t("dashboard", "sweepstakes.capacity.missing")),
                          isMin(1, t("dashboard", "sweepstakes.capacity.required")),
                          {
                            validate: (value: number | string | null | undefined) =>
                              Number.isInteger(Number(value)) || t("dashboard", "sweepstakes.capacity.required"),
                          },
                        ]
                      : undefined
                  }
                  data-test="ticketing-rates-seats-input"
                  onBlur={logUserInputEvent({
                    inputName: "seats",
                    isEmpty: !form.getValues("seats")?.trim?.(),
                    isValid: !form.formState.errors.seats,
                  })}
                />
                {isSweepstakes && requiresBaseEntryRate && (
                  <DashboardAmountInput
                    control={form.control}
                    name="sweepstakesBaseEntryRate"
                    locale={apiTicketing.locale}
                    currency={organizationCurrency}
                    label={t("dashboard", "sweepstakes.rates.baseRate.label")}
                    tooltipText={t("dashboard", "sweepstakes.rates.baseRate.tooltip")}
                    rules={[
                      isRequired(t("dashboard", "sweepstakes.rates.baseRate.required")),
                      isMin(0, t("dashboard", "sweepstakes.rates.baseRate.required")),
                    ]}
                    errorMessage={errors.sweepstakesBaseEntryRate?.message}
                    data-test="sweepstakes-base-entry-rate"
                  />
                )}
              </Stack>
            </LiveEditorBlock>
          )}

          <Stack gap={2}>
            <RatesSection
              form={form}
              onRateCollapsed={onRateCollapsed}
              imageEditorsRegistry={rateImageEditorsRegistry}
            />
            {isSweepstakes && (
              <Stack gap={2} data-test="sweepstakes-free-entry-guideline">
                <DashboardDivider />
                <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                  {t("dashboard", "sweepstakes.rates.freeEntryHelper")}{" "}
                  <DashboardLink
                    variant="body2"
                    underline="always"
                    href={SWEEPSTAKES_FREE_ENTRY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "inherit", textDecorationColor: "currentColor" }}
                  >
                    {t("common", "learnMore")}
                  </DashboardLink>
                </DashboardTypography>
              </Stack>
            )}
          </Stack>

          <CapacityTotalsDialog
            open={isCapacityDialogOpen}
            shortfall={capacityShortfall}
            formCategory={formCategory}
            isSweepstakes={Boolean(isSweepstakes)}
            isRegistration={isRegistration}
            isSubmitting={form.formState.isSubmitting}
            onConfirm={handleCapacityConfirm}
            onAdjust={handleCapacityAdjust}
          />
        </Stack>
      </FormProvider>
    </FormStepContainer>
  );
};
